import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './UserSearch.css';

function UserSearch({ onFollowChange }) {
  const { user: currentUser } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [followingMap, setFollowingMap] = useState({});
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  let searchTimeout = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const checkFollowStatus = async (userId) => {
    try {
      const response = await axios.get(`/api/follow/user/${userId}/is-following`);
      setFollowingMap(prev => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const searchUsers = async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get('/api/users/search', { params: { query: searchQuery } });
      const users = response.data.filter(u => u.id !== currentUser.userId);
      setResults(users);
      setShowResults(true);
      users.forEach(u => checkFollowStatus(u.id));
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchUsers(value.trim()), 300);
  };

  const handleFollow = async (userId) => {
    try {
      await axios.post(`/api/follow/user/${userId}`);
      const statusResponse = await axios.get(`/api/follow/user/${userId}/is-following`);
      setFollowingMap(prev => ({ ...prev, [userId]: statusResponse.data }));
      if (onFollowChange) onFollowChange();
    } catch (error) {
      console.error('Error toggling follow:', error);
      alert(error.response?.data?.message || 'Failed to update follow status');
    }
  };

  return (
    <div className="user-search" ref={searchRef}>
      <input
        type="text"
        className="user-search-input"
        placeholder="Search users to follow..."
        value={query}
        onChange={handleInputChange}
        onFocus={() => query.length >= 2 && setShowResults(true)}
      />

      {showResults && (
        <div className="user-search-results">
          {loading && <div className="search-status">Searching...</div>}
          {!loading && results.length === 0 && (
            <div className="search-status">No users found</div>
          )}
          {results.map(u => (
            <div key={u.id} className="search-result-item">
              <Link to={`/profile/${u.id}`} className="search-result-info" onClick={() => setShowResults(false)}>
                <div className="search-result-name">{u.username}</div>
                <div className="search-result-meta">
                  {u.followersCount} followers · {u.postsCount} posts
                </div>
              </Link>
              <button
                className={`search-follow-btn ${followingMap[u.id] ? 'following' : ''}`}
                onClick={() => handleFollow(u.id)}
              >
                {followingMap[u.id] ? 'Following' : 'Follow'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserSearch;
